import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { IPartnerDocumentSection, parseQbrDocumentSettings } from '../../../lib/documentsAndLinks'
import { getSelectedCockpitQuarters } from '../../../lib/keyInitiativesTile'
import { useFilteredCockpitPartnerData } from '../../../model/hooks'
import {
  selectCockpitDocumentPartnerSections,
  selectCockpitPeriods,
  selectCockpitPlanningPeriod,
  selectEffectiveCockpitFYear,
} from '../../../model/selectors'

interface IUseCockpitDocumentsDataArgs {
  fallbackPartnerId?: string
  isManagerCockpitMode?: boolean
}

interface IUseCockpitDocumentsDataResult {
  sections: IPartnerDocumentSection[]
}

const EMPTY_SECTION = (partnerId: string): IPartnerDocumentSection => ({
  partnerId,
  partnerName: partnerId,
  rtm: null,
  eltDocuments: [],
  qbrDocuments: [],
})

export const useCockpitDocumentsData = ({
  fallbackPartnerId,
  isManagerCockpitMode = false,
}: IUseCockpitDocumentsDataArgs = {}): IUseCockpitDocumentsDataResult => {
  const sections = useSelector(selectCockpitDocumentPartnerSections)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const planningPeriod = useSelector(selectCockpitPlanningPeriod)
  const effectiveFYear = useSelector(selectEffectiveCockpitFYear)
  const filteredPartnerData = useFilteredCockpitPartnerData()

  const withFallback = useMemo(() => {
    const selectedQuarters = getSelectedCockpitQuarters(selectedPeriods, planningPeriod?.fQuarter)
    const sectionsForPartners = isManagerCockpitMode
      ? sections.filter((section) =>
          filteredPartnerData.some((partner) => partner.PartnerId === section.partnerId)
        )
      : sections
    const sectionsInScope = sectionsForPartners.map((section) => ({
      ...section,
      eltDocuments:
        selectedQuarters === null
          ? section.eltDocuments
          : section.eltDocuments.filter((document) =>
              selectedQuarters.some((quarter) => quarter === document.FQuarter)
            ),
      qbrDocuments:
        selectedQuarters === null
          ? section.qbrDocuments
          : section.qbrDocuments.filter((document) => {
              const { selectedPeriods: documentPeriods } = parseQbrDocumentSettings(
                document.Settings
              )
              const hasSelectedPeriods = Boolean(documentPeriods?.length)
              const documentQuarters = hasSelectedPeriods
                ? (documentPeriods?.find(({ fYear }) => fYear === effectiveFYear)?.fQuarters ?? [])
                : document.FYear === effectiveFYear && document.FQuarter !== null
                  ? [document.FQuarter]
                  : []

              return documentQuarters.some((documentQuarter) =>
                selectedQuarters.some((selectedQuarter) => selectedQuarter === documentQuarter)
              )
            }),
    }))

    if (!fallbackPartnerId) {
      return sectionsInScope
    }

    const normalized = fallbackPartnerId.trim()
    const hasSection = sectionsInScope.some((section) => section.partnerId === normalized)

    return hasSection ? sectionsInScope : [...sectionsInScope, EMPTY_SECTION(normalized)]
  }, [
    fallbackPartnerId,
    effectiveFYear,
    filteredPartnerData,
    planningPeriod?.fQuarter,
    isManagerCockpitMode,
    sections,
    selectedPeriods,
  ])

  return {
    sections: withFallback,
  }
}
